import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Project } from '../../types/project';
import { removeCollaborator, leaveProject } from '../../services/api';

interface CollaboratorManagerProps {
  projectId: string;
  project: Project;
  isOwner: boolean;
}

interface CollaboratorInfo {
  userId: string;
  email: string;
  displayName?: string;
}

export const CollaboratorManager: React.FC<CollaboratorManagerProps> = ({
  projectId,
  project,
  isOwner,
}) => {
  const [collaborators, setCollaborators] = useState<CollaboratorInfo[]>([]);
  const [ownerInfo, setOwnerInfo] = useState<CollaboratorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);

  // Fetch user details for collaborators and owner
  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch owner info
        const ownerQuery = query(collection(db, 'users'), where('__name__', '==', project.ownerId));
        const ownerSnapshot = await getDocs(ownerQuery);

        if (!ownerSnapshot.empty) {
          const ownerData = ownerSnapshot.docs[0].data();
          setOwnerInfo({
            userId: project.ownerId,
            email: ownerData.email || '',
            displayName: ownerData.displayName,
          });
        }

        // Fetch collaborator info
        if (project.collaborators && project.collaborators.length > 0) {
          const collabInfos: CollaboratorInfo[] = [];

          for (const userId of project.collaborators) {
            const userQuery = query(collection(db, 'users'), where('__name__', '==', userId));
            const userSnapshot = await getDocs(userQuery);

            if (!userSnapshot.empty) {
              const userData = userSnapshot.docs[0].data();
              collabInfos.push({
                userId,
                email: userData.email || '',
                displayName: userData.displayName,
              });
            }
          }

          setCollaborators(collabInfos);
        } else {
          setCollaborators([]);
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load collaborator information');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [project.ownerId, project.collaborators]);

  // Handle remove collaborator
  const handleRemoveCollaborator = async (userId: string) => {
    setRemovingUserId(userId);
    setError(null);

    try {
      await removeCollaborator(projectId, userId);
      setShowRemoveConfirm(null);
    } catch (err) {
      console.error('Error removing collaborator:', err);
      setError('Failed to remove collaborator. Please try again.');
    } finally {
      setRemovingUserId(null);
    }
  };

  // Handle leave project
  const handleLeaveProject = async () => {
    setIsLeaving(true);
    setError(null);

    try {
      await leaveProject(projectId);
      // Navigate away will happen automatically via useProject hook detecting permission change
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Error leaving project:', err);
      setError('Failed to leave project. Please try again.');
      setIsLeaving(false);
    }
  };

  // Get initials for avatar
  const getInitials = (name?: string, email?: string): string => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <p className="mt-2 text-gray-600">Loading collaborators...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Collaborators</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Collaborators List */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {/* Owner */}
        {ownerInfo && (
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {getInitials(ownerInfo.displayName, ownerInfo.email)}
                </span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">
                  {ownerInfo.displayName || ownerInfo.email}
                </p>
                {ownerInfo.displayName && (
                  <p className="text-sm text-gray-500">{ownerInfo.email}</p>
                )}
              </div>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              Owner
            </span>
          </div>
        )}

        {/* Collaborators */}
        {collaborators.length > 0 ? (
          collaborators.map((collaborator) => (
            <div key={collaborator.userId} className="p-4 flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">
                    {getInitials(collaborator.displayName, collaborator.email)}
                  </span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">
                    {collaborator.displayName || collaborator.email}
                  </p>
                  {collaborator.displayName && (
                    <p className="text-sm text-gray-500">{collaborator.email}</p>
                  )}
                </div>
              </div>
              {isOwner && (
                <button
                  onClick={() => setShowRemoveConfirm(collaborator.userId)}
                  disabled={removingUserId === collaborator.userId}
                  className="inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {removingUserId === collaborator.userId ? 'Removing...' : 'Remove'}
                </button>
              )}
            </div>
          ))
        ) : (
          !isOwner && (
            <div className="p-4 text-center text-gray-500 text-sm">
              No other collaborators
            </div>
          )
        )}

        {/* Empty state for owner with no collaborators */}
        {isOwner && collaborators.length === 0 && (
          <div className="p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No collaborators yet</p>
            <p className="text-xs text-gray-400">Invite team members to collaborate on this project</p>
          </div>
        )}
      </div>

      {/* Leave Project Button (for collaborators only) */}
      {!isOwner && (
        <div className="pt-4">
          <button
            onClick={() => setShowLeaveConfirm(true)}
            disabled={isLeaving}
            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {isLeaving ? 'Leaving...' : 'Leave Project'}
          </button>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Remove Collaborator</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to remove this collaborator? They will lose access to this project immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={() => handleRemoveCollaborator(showRemoveConfirm)}
                  disabled={removingUserId === showRemoveConfirm}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {removingUserId === showRemoveConfirm ? 'Removing...' : 'Remove'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRemoveConfirm(null)}
                  disabled={removingUserId === showRemoveConfirm}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Leave Project</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to leave this project? You will lose access immediately and will need to be re-invited to access it again.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={handleLeaveProject}
                  disabled={isLeaving}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isLeaving ? 'Leaving...' : 'Leave'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowLeaveConfirm(false)}
                  disabled={isLeaving}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
